import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { redirectAfterAuth } from "@/lib/authRedirect";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import SEO from "@/components/site/SEO";

/**
 * Onboarding — "Pilih Ruang Anda"
 * First-touch experience after signup/login for users where `onboarded !== true`.
 * User selects ≥1 Ruang across Grup A (Sekolah) + Grup B (Pendidikan & Kehidupan Anak).
 * On submit: POST /me/onboarding → refresh auth → redirect to Beranda personal.
 */
export default function Onboarding() {
  const nav = useNavigate();
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState({ grup_a: null, grup_b: null });
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  // Redirect guests
  useEffect(() => {
    if (!localStorage.getItem("k12_token") && !user) {
      nav("/login", { replace: true });
    }
  }, [user, nav]);

  // Load 16 Ruang AND any Ruang the user has ALREADY joined so the picker
  // pre-checks them (returning users who somehow land here don't see "Gabung 0").
  useEffect(() => {
    Promise.all([
      api.get("/ruang"),
      api.get("/me/memberships").catch(() => ({ data: { ruang_ids: [] } })),
    ]).then(([rGroups, rMem]) => {
      setGroups({ grup_a: rGroups.data.grup_a, grup_b: rGroups.data.grup_b });
      const existing = new Set(rMem.data.ruang_ids || []);
      if (existing.size > 0) setSelected(existing);
    }).catch(() => toast.error("Gagal memuat Ruang. Muat ulang halaman.")).finally(() => setLoading(false));
  }, []);

  const firstName = useMemo(() => {
    const n = (user?.name || "").trim().split(/\s+/)[0];
    return n || "orang tua";
  }, [user]);

  const toggle = (rid) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(rid)) nx.delete(rid); else nx.add(rid);
      return nx;
    });
  };

  const count = selected.size;

  const submit = async () => {
    if (count === 0) {
      toast.error("Pilih minimal 1 Ruang untuk melanjutkan.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/me/onboarding", { ruang_ids: [...selected] });
      await checkAuth();
      toast.success(`Anda bergabung ke ${count} Ruang. Selamat datang!`);
      // Prefer the pre-auth return_to (e.g., they clicked a share link) — else Beranda personal.
      redirectAfterAuth(nav, "/forum");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pilihan.");
    } finally {
      setSubmitting(false);
    }
  };

  const skip = () => {
    // Skip without marking onboarded — user will be re-prompted next login.
    redirectAfterAuth(nav, "/forum");
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-alabaster">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-alabaster" data-testid="onboarding-page">
      <SEO title="Pilih Ruang Anda — The School Reports" description="Tandai jenjang anak & topik yang Anda pikirkan." />
      <div className="max-w-2xl mx-auto px-5 pt-8 pb-40">
        {/* Header */}
        <p className="eyebrow text-brand-accent mb-3" data-testid="onboarding-welcome-kicker">
          SELAMAT DATANG, {firstName.toUpperCase()}
        </p>
        <h1 className="font-serif-display text-4xl md:text-5xl text-brand-primary leading-tight mb-3">
          Pilih Ruang Anda
        </h1>
        <p className="text-brand-text-muted text-base leading-relaxed mb-8">
          Tandai jenjang anak &amp; topik yang Anda pikirkan. Ini akan jadi beranda forum Anda.
        </p>

        {/* Grup A */}
        <RuangGroup
          title="Sekolah"
          helper="sesuai jenjang anak"
          items={groups.grup_a?.items || []}
          selected={selected}
          onToggle={toggle}
        />

        {/* Grup B */}
        <RuangGroup
          title="Pendidikan &amp; Kehidupan Anak"
          items={groups.grup_b?.items || []}
          selected={selected}
          onToggle={toggle}
          className="mt-10"
        />
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 bg-gradient-to-t from-brand-alabaster via-brand-alabaster/95 to-transparent pt-8 pb-6 px-5">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={submit}
            disabled={count === 0 || submitting}
            data-testid="onboarding-submit-btn"
            className="w-full h-14 rounded-full bg-brand-accent hover:bg-brand-accent-hover disabled:bg-brand-accent/40 disabled:cursor-not-allowed text-white font-semibold text-base transition-colors shadow-sm"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Menyimpan…</span>
            ) : (
              <>Gabung {count} Ruang <span className="opacity-80 mx-1">→</span> Mulai</>
            )}
          </button>
          <button
            onClick={skip}
            data-testid="onboarding-skip-btn"
            className="w-full mt-3 h-10 text-sm text-brand-text-muted hover:text-brand-text underline-offset-4 hover:underline"
          >
            Lewati dulu
          </button>
        </div>
      </div>
    </div>
  );
}

function RuangGroup({ title, helper, items, selected, onToggle, className = "" }) {
  return (
    <section className={className}>
      <div className="flex items-baseline justify-between mb-4">
        <h2
          className="font-serif-display text-2xl text-brand-primary"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {helper && <span className="text-xs text-brand-text-muted">{helper}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {items.map((r) => {
          const isSel = selected.has(r.ruang_id);
          return (
            <button
              key={r.ruang_id}
              type="button"
              onClick={() => onToggle(r.ruang_id)}
              data-testid={`onboarding-ruang-${r.slug}`}
              className={`group relative flex items-start gap-3 rounded-2xl border p-4 text-left transition-all ${
                isSel
                  ? "border-brand-accent bg-white shadow-sm ring-2 ring-brand-accent/20"
                  : "border-brand-border bg-white hover:border-brand-primary/30"
              }`}
            >
              <span className="text-2xl leading-none shrink-0" aria-hidden>{r.icon}</span>
              <span className="flex-1 min-w-0 pr-6">
                <span className="block font-semibold text-brand-text leading-snug text-sm">{r.name}</span>
                <span className="block text-[11px] text-brand-text-muted mt-1" data-testid={`onboarding-count-${r.slug}`}>
                  {formatOnboardCount(r.topic_count ?? r.discussion_count ?? 0)} topik · {formatOnboardCount(r.member_count)} anggota
                </span>
              </span>
              {/* Checkmark circle */}
              <span
                className={`absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center text-white transition-all ${
                  isSel ? "bg-brand-accent scale-100 opacity-100" : "bg-brand-border/60 scale-90 opacity-0 group-hover:opacity-100"
                }`}
              >
                {isSel && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function formatOnboardCount(n) {
  const num = Number(n || 0);
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}rb`;
  return String(num);
}
